import classNames from 'classnames';
import * as _ from 'lodash-es';
import moment from 'moment';
import React, { useState } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import AvatarStack from '../../components/AvatarStack';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import PopOverPrimitive from '../../components/PopOverPrimitive';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import UpgradeBadge from '../../organization/UpgradeBadge';
import { useGetUser } from '../../user/useAllUsers';
import getFeature from '../../utils/features';
import Link from '../../utils/router/Link';
import postPath from '../../utils/url/postPath';
import { openPricingModal } from '../../utils/url/pricingPath';
import Stats from '../Stats';
import type { HistoryGroup } from './partitionAndGroupHistories';
import partitionAndGroupHistories from './partitionAndGroupHistories';
import sumHistoryStats from './sumHistoryStats';
import * as styles from './VersionChangesDropdown.module.scss';

const VERSION_CHANGES_DROPDOWN_POST_FRAGMENT = gql(`
  fragment VersionChangesDropdown_post on Post {
    id
    histories {
      id
      ...partitionAndGroupHistories_history
      ...sumHistoryStats_history
    }
    ...postPath_post
  }  
`);

interface SectionTitleProps {
  children: React.ReactNode;
  badge?: React.ReactNode;
}

const SectionTitle = ({ children, badge }: SectionTitleProps) => {
  return (
    <div className={styles.sectionTitle}>
      <div>{children}</div>
      {!!badge && <div className={styles.sectionTitleBadge}>{badge}</div>}
    </div>
  );
};

const CalendarDay = ({ date }: { date: moment.Moment }) => {
  return (
    <div className={styles.calendarDay}>
      <div className={styles.calendarDayWeekday}>{date.format('ddd')}</div>
      <div>{date.format('D')}</div>
    </div>
  );
};

interface VersionChangeRowProps {
  history: HistoryGroup<
    DocumentType<
      typeof VERSION_CHANGES_DROPDOWN_POST_FRAGMENT
    >['histories'][number]
  >;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  to: string;
  onClick?: (e: React.MouseEvent) => void;
  getUser: ReturnType<typeof useGetUser>;
}

const VersionChangeRow = ({
  history,
  setIsOpen,
  to,
  onClick,
  getUser,
}: VersionChangeRowProps) => {
  const insertedAt = moment(history.insertedAt);

  const time = insertedAt.format('h:mma');
  const date = insertedAt.format('MMMM Do');

  const historyStats = sumHistoryStats(history.histories);

  return (
    <Link
      to={to}
      onClick={(e) => {
        setIsOpen(false);
        onClick?.(e);
      }}
      className={styles.dropdownRow}
    >
      <div>
        <CalendarDay date={insertedAt} />
      </div>

      <div className={styles.dropdownRowContent}>
        <div className={styles.dropdownRowTitle}>
          {time}, {date}
        </div>
        <div className={styles.dropdownRowSubtitle}>
          <Stats
            stats={historyStats}
            maxValues={{ insert: 999, delete: 999, comment: 99, resolve: 99 }}
          />
        </div>
      </div>

      <div>
        <AvatarStack items={history.authors.map(getUser)} limit={2} />
      </div>
    </Link>
  );
};

interface TriggerButtonProps {
  children: React.ReactNode;
  disabled: boolean;
  open: boolean;
}

const TriggerButton = ({ children, disabled, open }: TriggerButtonProps) => {
  return (
    <PopOverPrimitive.Trigger asChild>
      <Button
        className={classNames(styles.versionButton, {
          [styles.active]: open,
        })}
        disabled={disabled}
      >
        <span>{children}</span>
        <span className={styles.versionButtonCaret}>
          <Caret direction={open ? 'up' : 'down'} size={8} />
        </span>
      </Button>
    </PopOverPrimitive.Trigger>
  );
};

interface VersionChangesDropdownProps {
  post: DocumentType<typeof VERSION_CHANGES_DROPDOWN_POST_FRAGMENT>;
  version?: number;
  initialIsOpen?: boolean;
}

const VersionChangesDropdown = ({
  post,
  version,
  initialIsOpen = false,
}: VersionChangesDropdownProps) => {
  const [isOpen, setIsOpen] = useState(initialIsOpen);

  const dispatch = useAppDispatch();
  const getUser = useGetUser();
  const versionHistoryRestrictionDays = getFeature('version_history');

  const { accessibleHistories, restrictedHistories } =
    partitionAndGroupHistories(post.histories);

  const [
    restrictedHistoriesWithinOneYear,
    restrictedHistoriesOlderThanOneYear,
  ] = _.partition(
    restrictedHistories,
    (history) =>
      new Date(history.insertedAt) > moment().subtract(1, 'year').toDate(),
  );

  const historyRowsFromCurrentDay = accessibleHistories.filter((row) =>
    moment(row.insertedAt).isSame(moment(), 'day'),
  );

  const historyRowsFromOtherDays = accessibleHistories.filter(
    (row) => !moment(row.insertedAt).isSame(moment(), 'day'),
  );

  const accessibleGroupedHistoryRows = Object.entries(
    _.groupBy(historyRowsFromOtherDays, (row) =>
      moment(row.insertedAt).format('YYYY-MM'),
    ),
  )
    .toSorted(([a], [b]) => moment(b).diff(a))
    .map(([month, groupRows]) => {
      const monthMoment = moment(month);
      return {
        groupRows,
        title: monthMoment.isSame(moment(), 'year')
          ? monthMoment.format('MMMM')
          : monthMoment.format('MMMM YYYY'),
      };
    });

  if (historyRowsFromCurrentDay.length > 0) {
    accessibleGroupedHistoryRows.unshift({
      groupRows: historyRowsFromCurrentDay,
      title: 'Today',
    });
  }

  return (
    <PopOverPrimitive
      offset={4}
      onOpenChange={setIsOpen}
      open={isOpen}
      placement="bottom-start"
      trigger="click"
      autoUpdateAnimationFrame
      noFlip
      hideWhenReferenceHidden
    >
      <TriggerButton
        open={isOpen}
        disabled={
          accessibleHistories.length === 0 && restrictedHistories.length === 0
        }
      >
        {version ? `Version ${version}` : <>Select a version&hellip;</>}
      </TriggerButton>
      <PopOverPrimitive.Floating>
        <PopOverPrimitive.Content className={styles.dropdownContent}>
          {accessibleGroupedHistoryRows.map(({ groupRows, title }) => (
            <React.Fragment key={title}>
              <SectionTitle>{title}</SectionTitle>
              {groupRows.map((history) => {
                return (
                  <VersionChangeRow
                    setIsOpen={setIsOpen}
                    key={`${history.versionFrom}-${history.versionTo}`}
                    history={history}
                    getUser={getUser}
                    to={postPath(post, {
                      histories: `${history.versionFrom}-${history.versionTo}`,
                    })}
                  />
                );
              })}
            </React.Fragment>
          ))}

          {restrictedHistoriesWithinOneYear.length > 0 && (
            <>
              <Button
                onClick={() => {
                  setIsOpen(false);
                  openPricingModal(dispatch, 'versionHistory');
                }}
              >
                <SectionTitle badge={<UpgradeBadge />}>
                  Older than {versionHistoryRestrictionDays} days
                </SectionTitle>
              </Button>
              <div className={styles.restrictedHistories}>
                {restrictedHistoriesWithinOneYear.map((history) => {
                  return (
                    <VersionChangeRow
                      setIsOpen={setIsOpen}
                      key={`${history.versionFrom}-${history.versionTo}`}
                      history={history}
                      getUser={getUser}
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openPricingModal(dispatch, 'versionHistory');
                      }}
                    />
                  );
                })}
              </div>
            </>
          )}

          {restrictedHistoriesOlderThanOneYear.length > 0 && (
            <>
              <Button
                onClick={() => {
                  setIsOpen(false);
                  openPricingModal(dispatch, 'versionHistory');
                }}
              >
                <SectionTitle badge={<UpgradeBadge />}>
                  Older than {Math.max(versionHistoryRestrictionDays, 365)} days
                </SectionTitle>
              </Button>
              <div className={styles.restrictedHistories}>
                {restrictedHistoriesOlderThanOneYear.map((history) => {
                  return (
                    <VersionChangeRow
                      setIsOpen={setIsOpen}
                      key={`${history.versionFrom}-${history.versionTo}`}
                      history={history}
                      getUser={getUser}
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openPricingModal(dispatch, 'versionHistory');
                      }}
                    />
                  );
                })}
              </div>
            </>
          )}
        </PopOverPrimitive.Content>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

export default VersionChangesDropdown;
